import Route from '@ember/routing/route';
import { service } from '@ember/service';
import {
  isPslfDebugActive,
  getPslfDebugState,
  buildPslfDataOverrides,
  buildMockLoanSummary,
  debugNsldsFileRequestDate,
  DEBUG_RECOMMENDATION,
} from 'tio-employee/utils/pslf/debug-scenarios';
import type { PslfDebugState } from 'tio-employee/utils/pslf/debug-scenarios';
import type PersonModel from 'tio-common/models/person';
import type PslfProfileModel from 'tio-common/models/pslf-profile';
import type PslfGroup from 'tio-common/models/pslf-group';
import type ObservableDocumentModel from 'tio-common/models/observable-document';
import type { NsldsStructuredContent } from 'tio-common/models/observable-document';
import type RecommendationModel from 'tio-common/models/recommendation';
// This is for types. Anything we have custom may show an error.
import type StoreService from 'ember-data/store';
import type SessionContextService from 'tio-employee/services/session-context';
import type ReimaginedDataService from 'tio-employee/services/reimagined-data';
import { accountsToLiveAccounts } from 'tio-employee/services/reimagined-data';
import type { LivePslfData } from 'tio-employee/services/reimagined-data';
import type FeaturesService from 'tio-common/services/features';

export interface PslfDebugOverrides {
  linkLoans: boolean;
}

export default class AuthenticatedPslfDashboardIndexRoute extends Route {
  @service declare store: StoreService;
  @service declare sessionContext: SessionContextService;
  @service declare reimaginedData: ReimaginedDataService;
  @service declare features: FeaturesService;

  queryParams = {
    v2: { refreshModel: true },
  };

  async model(params: { v2?: string }) {
    const isV2 = params.v2 === 'true';
    const userId = this.sessionContext.user.id;
    const personId = this.sessionContext.user.person.id;
    const parentModel = this.modelFor('authenticated.pslf') as {
      pslfProfile: PslfProfileModel;
      groups: PslfGroup[];
    };

    const [loanSummary, person, mostRecentRecQuery] = await Promise.all([
      // @ts-expect-error - Ember Data adapter method not typed on MinimumAdapterInterface
      this.store.adapterFor('user').getLoanSummary(userId),
      // @ts-expect-error type models
      this.store.findRecord<PersonModel>('person', personId, {
        include: 'accounts.financial-institution,accounts.loans',
        reload: true,
      }),
      this.store.query<RecommendationModel>('recommendation', {
        filter: {
          user: userId,
          'requesting-app': 'PSLF',
        },
        include: 'user',
        sort: '-created-at',
        page: {
          offset: 0,
          limit: 1,
        },
      }),
    ]);

    // Base model (always returned)
    const baseModel = {
      accounts: person.accounts,
      loanSummary: loanSummary,
      mostRecentRecommendation: mostRecentRecQuery?.[0] as RecommendationModel | undefined,
      person: person,
      pslfProfile: parentModel.pslfProfile,
      isV2,
      // v2-only fields (populated below when isV2 is true)
      pslfData: undefined as LivePslfData | undefined,
      nsldsFileRequestDate: null as string | null,
      debugOverrides: undefined as PslfDebugOverrides | undefined,
    };

    if (!isV2) {
      return baseModel;
    }

    // v2 mode: fetch additional data
    const [pslfData, nsldsDocuments] = await Promise.all([
      this.reimaginedData.fetchPslfData(),
      this.store.query<ObservableDocumentModel>('observable-document', {
        filter: {
          person: personId,
          provider: 'ObservableProvider.NSLDS',
        },
        sort: '-created-at',
        page: { limit: 1 },
      }),
    ]);

    // Extract file_request_date from the most recent NSLDS document
    const mostRecentNslds = nsldsDocuments?.[0];
    if (mostRecentNslds) {
      const content = mostRecentNslds.structuredContent as NsldsStructuredContent | undefined;
      baseModel.nsldsFileRequestDate = content?.file?.file_request_date ?? null;
    }

    baseModel.pslfData = pslfData;

    // Apply PSLF debug overrides when ?pslf_debug=true (only in non-production builds).
    // The pslfDebugPanel feature flag is false in production, so even stale localStorage
    // state on a real customer's device cannot trigger the override path.
    if (this.features.isEnabled('pslfDebugPanel') && isPslfDebugActive()) {
      const debugState = getPslfDebugState();

      baseModel.pslfData = buildPslfDataOverrides(debugState, pslfData, this.store);
      baseModel.loanSummary = buildMockLoanSummary(debugState);

      if (debugState.estimatorCompleted || debugState.recertifyIncome) {
        baseModel.mostRecentRecommendation = this.buildDebugRecommendation();
      } else {
        baseModel.mostRecentRecommendation = undefined;
      }

      if (debugState.hasNsldsData || debugState.annualReminder) {
        baseModel.nsldsFileRequestDate = debugNsldsFileRequestDate();
      }

      if (debugState.hasNsldsData || debugState.annualReminder) {
        this.pushDebugAccountsIntoStore(person, debugState);
        // Re-derive pslfData.accounts from the freshly-pushed store records so we
        // exercise the same mapping live data uses (accountsToLiveAccounts).
        // This eliminates the "two parallel mock paths can drift" class of bug.
        if (baseModel.pslfData) {
          baseModel.pslfData = {
            ...baseModel.pslfData,
            accounts: accountsToLiveAccounts(person.accounts ?? []),
          };
        }
      } else if (baseModel.pslfData) {
        // Debug mode + NSLDS off => simulate "no loans linked" regardless of any
        // real accounts the user has in the store.
        baseModel.pslfData = { ...baseModel.pslfData, accounts: [] };
      }

      baseModel.debugOverrides = {
        linkLoans: debugState.linkLoans,
      };
    }

    return baseModel;
  }

  /**
   * Push mock Account and Loan records into the Ember Data store so that
   * person.accounts returns debug data. Unloads existing accounts first
   * to avoid mixing real and mock data.
   */
  private pushDebugAccountsIntoStore(person: PersonModel, debugState: PslfDebugState) {
    const accountId = `debug-nslds-${person.id}`;

    // Unload existing accounts to avoid mixing live and debug data
    const existingAccounts = person.accounts?.slice() ?? [];
    for (const account of existingAccounts) {
      for (const loan of account.loans?.slice() ?? []) {
        loan.unloadRecord();
      }
      account.unloadRecord();
    }

    // Build JSON:API loan resources
    const loanResources = debugState.debugLoans.map((debugLoan, i) => ({
      id: `debug-loan-${i + 1}`,
      type: 'loan',
      attributes: {
        isActive: true,
        observation: {
          'loan-name': debugLoan.name,
          balance: debugLoan.currentBalance,
          'principal-balance': debugLoan.currentBalance,
          'interest-rate': 5.5,
          'forgiveness-program-qualified': debugLoan.qualifyingPayments,
          'is-paid-off': false,
          'loan-type': debugLoan.loanType,
          'repayment-plan-derived': 'ibr',
          status: 'active',
        },
      },
      relationships: {
        account: { data: { type: 'account', id: accountId } },
      },
    }));

    // Push account + loans into the store
    this.store.push({
      data: {
        id: accountId,
        type: 'account',
        attributes: {
          isActive: true,
          observation: {
            provider: 'ObservableProvider.NSLDS',
            balance: debugState.debugLoans.reduce((sum, l) => sum + l.currentBalance, 0),
          },
        },
        relationships: {
          person: { data: { type: 'person', id: person.id } },
          loans: {
            data: loanResources.map((l) => ({ type: 'loan', id: l.id })),
          },
        },
      },
      included: loanResources,
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } as any);
  }

  /**
   * Push a debug RecommendationModel through the store so it has store identity
   * and exercises the same getters (formattedBestPlanSummary, estimatedMonthlyPayment, etc.)
   * the live model uses. Avoids the `as unknown as RecommendationModel` cast which
   * masks bugs that only surface when component code touches store-backed fields.
   */
  private buildDebugRecommendation(): RecommendationModel {
    const id = 'debug-recommendation';
    // Unload any prior debug record to avoid duplicate-id push errors on hot-reload
    const existing = this.store.peekRecord('recommendation', id) as RecommendationModel | undefined;
    if (existing) existing.unloadRecord();

    this.store.push({
      data: {
        id,
        type: 'recommendation',
        attributes: {
          'has-error-in-calcs': false,
          'requesting-app': 'PSLF',
          'created-at': new Date().toISOString(),
          'best-plan-summary': {
            'payoff-amount': [DEBUG_RECOMMENDATION.bestPayoffAmount],
            'term-months': [DEBUG_RECOMMENDATION.bestTermMonths],
            'monthly-payments': [DEBUG_RECOMMENDATION.bestMonthlyPayments],
            'total-interest-paid': [DEBUG_RECOMMENDATION.bestTotalInterestPaid],
            'estimated-amount-forgiven': [DEBUG_RECOMMENDATION.bestRemLoanBalance],
          },
          'current-plan-summary': {
            'payoff-amount': [DEBUG_RECOMMENDATION.currentPayoffAmount],
            'term-months': [DEBUG_RECOMMENDATION.currentTermMonths],
            'monthly-payments': [DEBUG_RECOMMENDATION.currentMonthlyPayments],
            'total-interest-paid': [DEBUG_RECOMMENDATION.currentTotalInterestPaid],
            'estimated-amount-forgiven': [DEBUG_RECOMMENDATION.currentRemLoanBalance],
          },
          'best-plan-next-steps': [],
          'best-plan-table-headings': [],
          'current-plan-table-headings': [],
          'input-summary': {},
          'output-summary': {},
        },
      },
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } as any);

    return this.store.peekRecord('recommendation', id) as RecommendationModel;
  }
}
